import { useEffect } from "react";
import { X, Plus, Minus, Trash2, ShoppingBag, MessageCircle } from "lucide-react";
import { useCart } from "../context/CartContext";

const formatter = new Intl.NumberFormat("es-MX", {
  style: "currency",
  currency: "MXN",
  maximumFractionDigits: 0,
});

export const CartDrawer = () => {
  const {
    items,
    drawerOpen,
    closeDrawer,
    incrementItem,
    decrementItem,
    removeItem,
    clearCart,
    totalItems,
    totalPrice,
    buildWhatsAppUrl,
  } = useCart();

  // Lock body scroll when drawer open
  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  const handleCheckout = () => {
    if (items.length === 0) return;
    const url = buildWhatsAppUrl();
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      {/* Overlay */}
      <div
        onClick={closeDrawer}
        data-testid="cart-overlay"
        className={`fixed inset-0 z-[60] bg-[#2B241D]/50 backdrop-blur-sm transition-opacity duration-300 ${
          drawerOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      />

      {/* Drawer */}
      <aside
        data-testid="cart-drawer"
        aria-hidden={!drawerOpen}
        className={`fixed top-0 right-0 z-[70] h-full w-full sm:w-[420px] bg-[#F4EFE6] shadow-2xl border-l border-[#E6DFD3] flex flex-col transition-transform duration-300 ease-out ${
          drawerOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#E6DFD3]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#F5EBD3] border border-[#E6DFD3] flex items-center justify-center">
              <ShoppingBag size={18} strokeWidth={1.5} className="text-[#D96C42]" />
            </div>
            <div>
              <div className="font-serif text-2xl text-[#2B241D] leading-none">
                Tu pedido
              </div>
              <div className="font-sans text-xs uppercase tracking-[0.2em] text-[#5C5146] mt-1">
                {totalItems} {totalItems === 1 ? "artículo" : "artículos"}
              </div>
            </div>
          </div>
          <button
            data-testid="cart-close-btn"
            onClick={closeDrawer}
            aria-label="Cerrar carrito"
            className="w-10 h-10 rounded-full border border-[#D5CBB8] text-[#2B241D] hover:bg-[#EAE3D5] flex items-center justify-center transition-colors"
          >
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {items.length === 0 ? (
            <div
              data-testid="cart-empty-state"
              className="h-full flex flex-col items-center justify-center text-center py-16"
            >
              <div className="w-20 h-20 rounded-full bg-[#EAE3D5] flex items-center justify-center mb-6">
                <ShoppingBag size={28} strokeWidth={1.5} className="text-[#5C5146]" />
              </div>
              <h3 className="font-serif text-2xl text-[#2B241D] mb-2">
                Tu carrito está vacío
              </h3>
              <p className="font-sans text-sm text-[#5C5146] max-w-xs font-light leading-relaxed">
                Explora el menú y añade tus pizzas favoritas para hacer tu
                pedido por WhatsApp.
              </p>
              <button
                data-testid="cart-empty-continue"
                onClick={closeDrawer}
                className="mt-8 inline-flex items-center gap-2 bg-[#2B241D] text-[#F5EBD3] px-6 py-3 rounded-full text-sm font-medium hover:bg-[#D96C42] transition-colors"
              >
                Ver el menú
              </button>
            </div>
          ) : (
            <ul className="space-y-4">
              {items.map((item) => (
                <li
                  key={item.key}
                  data-testid={`cart-item-${item.id}`}
                  className="bg-[#F5EBD3] rounded-2xl p-4 border border-[#E6DFD3] flex items-start gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-serif text-lg text-[#2B241D] leading-tight truncate">
                      {item.name}
                    </div>
                    <div className="font-sans text-xs text-[#5C5146] mt-1">
                      {formatter.format(item.price)} c/u
                    </div>

                    <div className="mt-3 flex items-center justify-between gap-3">
                      <div className="inline-flex items-center gap-1 bg-[#F4EFE6] rounded-full border border-[#E6DFD3] p-1">
                        <button
                          data-testid={`cart-decrement-${item.id}`}
                          onClick={() => decrementItem(item.key)}
                          aria-label="Quitar uno"
                          className="w-7 h-7 rounded-full flex items-center justify-center text-[#2B241D] hover:bg-[#EAE3D5] transition-colors"
                        >
                          <Minus size={14} strokeWidth={1.75} />
                        </button>
                        <span
                          data-testid={`cart-qty-${item.id}`}
                          className="font-sans text-sm font-medium text-[#2B241D] w-6 text-center"
                        >
                          {item.qty}
                        </span>
                        <button
                          data-testid={`cart-increment-${item.id}`}
                          onClick={() => incrementItem(item.key)}
                          aria-label="Añadir uno"
                          className="w-7 h-7 rounded-full flex items-center justify-center text-[#2B241D] hover:bg-[#EAE3D5] transition-colors"
                        >
                          <Plus size={14} strokeWidth={1.75} />
                        </button>
                      </div>

                      <div className="font-serif text-lg text-[#D96C42]">
                        {formatter.format(item.price * item.qty)}
                      </div>
                    </div>
                  </div>

                  <button
                    data-testid={`cart-remove-${item.id}`}
                    onClick={() => removeItem(item.key)}
                    aria-label={`Eliminar ${item.name}`}
                    className="w-8 h-8 rounded-full text-[#5C5146] hover:bg-[#F4EFE6] hover:text-[#D96C42] flex items-center justify-center transition-colors shrink-0"
                  >
                    <Trash2 size={16} strokeWidth={1.5} />
                  </button>
                </li>
              ))}

              {items.length > 0 && (
                <li>
                  <button
                    data-testid="cart-clear-all"
                    onClick={clearCart}
                    className="w-full font-sans text-xs uppercase tracking-[0.2em] text-[#5C5146] hover:text-[#D96C42] transition-colors py-2"
                  >
                    Vaciar carrito
                  </button>
                </li>
              )}
            </ul>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-[#E6DFD3] px-6 py-5 bg-[#F5EBD3]">
            <div className="flex items-baseline justify-between mb-4">
              <span className="font-sans text-xs uppercase tracking-[0.2em] text-[#5C5146]">
                Total estimado
              </span>
              <span
                data-testid="cart-total-price"
                className="font-serif text-3xl text-[#2B241D]"
              >
                {formatter.format(totalPrice)}
              </span>
            </div>
            <p className="font-sans text-[11px] text-[#5C5146] font-light leading-relaxed mb-4">
              Al continuar se abrirá WhatsApp con tu pedido listo para enviar.
              El equipo confirmará el total final y tiempo de entrega.
            </p>
            <button
              data-testid="cart-checkout-whatsapp"
              onClick={handleCheckout}
              className="w-full inline-flex items-center justify-center gap-2.5 bg-[#25D366] text-[#0B0B0B] px-6 py-4 rounded-full font-medium text-sm hover:bg-[#1EBE5D] transition-colors"
            >
              <MessageCircle size={18} strokeWidth={2} className="fill-[#0B0B0B]" />
              Enviar pedido por WhatsApp
            </button>
          </div>
        )}
      </aside>
    </>
  );
};
